import * as React from 'react';
import './styles.scss';
import styled from 'styled-components';
import { Chip } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ClientEntity } from "../../store/entities/state";
import { TK } from "../../store/translations/translationKeys";
import { Translate, useTranslations } from "../../store/translations/hooks";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import Typography from '../../components/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { ClientPack, PackSettings } from '../../models';
import { entityCreating, entityEditing, loadEntities } from '../../store/entities/actions';
import { getPacksForClient, getPacksForProfessional, isLoadingEntities } from '../../store/entities/selectors';
import { getClientPacksAsync, getProfessionalPacksAsync } from '../../ajax/requests';
import { daysBetween, getNowDateString, minutesToTimeString } from '../../shared/utils';
import { sanitize } from '../../shared/string';
import { canEditAppointments, canEditProfessionals, canSeePrices, canSeeInvoices } from '../../store/session/selectors';
import { getTotalPayments } from '../../shared/appointmint/appointment';
import { useTaxData } from '../../hooks/useTaxData';
import moment from 'moment';
import { usePackTemplates } from '../../hooks/usePackTemplates';
import AppButton from '../../components/AppButton';

export interface ClientPacksTableProps {
    isOpen?: boolean,
    client: ClientEntity,
    clientIsProfessional?: boolean,
}

type PackStatusTone = 'success' | 'warning' | 'danger' | 'neutral';

const getPackAvailabilityText = (t: Translate<TK>, pack: ClientPack) =>
    !!pack.totalMinutes && !pack.totalMinutesStillAvailable ? `(${t(TK.packExhausted)})`
    : !!pack.totalCredits && !pack.totalCreditsStillAvailable ? `(${t(TK.packExhausted)})`
    : (!!pack.validityFrom && daysBetween(new Date(), new Date(pack.validityFrom)) <= 0) ? `(${t(TK.notValidYet)})`
    : (!!pack.validityTo && daysBetween(new Date(), new Date(pack.validityTo)) > 1) ? `(${t(TK.expired)})`
    : !!pack.totalMinutesStillAvailable ? `(${t(TK.available)} ${minutesToTimeString(pack.totalMinutesStillAvailable)})`
    : !!pack.totalCreditsStillAvailable ? `(${t(TK.available)} ${pack.totalCreditsStillAvailable} ${t(TK.credits).toLocaleLowerCase()})`
    : ''

const getPackStatusTone = (pack: ClientPack): PackStatusTone => {
    if (!!pack.totalMinutes && !pack.totalMinutesStillAvailable) {
        return 'danger';
    }

    if (!!pack.totalCredits && !pack.totalCreditsStillAvailable) {
        return 'danger';
    }

    if (!!pack.validityFrom && daysBetween(new Date(), new Date(pack.validityFrom)) <= 0) {
        return 'warning';
    }

    if (!!pack.validityTo && daysBetween(new Date(), new Date(pack.validityTo)) > 1) {
        return 'neutral';
    }

    if (!!pack.totalMinutesStillAvailable || !!pack.totalCreditsStillAvailable) {
        return 'success';
    }

    return 'neutral';
}

const resolvePackAcquiredDate = (pack: ClientPack): string => pack.creationDate?.substr(0, 10) ?? pack.validityFrom?.substr(0, 10) ?? '';

const resolvePackValidityFrom = (pack: ClientPack): string => pack.validityFrom?.substr(0, 10) ?? pack.creationDate?.substr(0, 10) ?? '';

const resolvePackValidityTo = (pack: ClientPack): string => pack.validityTo?.substr(0, 10) ?? '';

const resolvePackSortValue = (pack: ClientPack): number => moment(pack.validityFrom ?? pack.creationDate ?? '').valueOf();
const resolvePackSortValueSafe = (pack: ClientPack): number => {
    const value = resolvePackSortValue(pack);
    return Number.isFinite(value) ? value : 0;
};

const resolvePackAccent = (tone: PackStatusTone): string => {
    switch (tone) {
        case 'success':
            return 'var(--brand-primary, #2ab7af)';
        case 'warning':
            return '#ea8c1a';
        case 'danger':
            return '#ea8c1a';
        default:
            return 'var(--appLigthGrey, #d1d5db)';
    }
}

const resolvePackBadgeStyles = (tone: PackStatusTone): { background: string; color: string; border: string } => {
    switch (tone) {
        case 'success':
            return {
                background: 'rgba(42, 183, 175, 0.12)',
                color: 'var(--brand-secondary, #009385)',
                border: 'rgba(42, 183, 175, 0.24)',
            };
        case 'warning':
            return {
                background: 'rgba(234, 140, 26, 0.12)',
                color: '#b45f06',
                border: 'rgba(234, 140, 26, 0.24)',
            };
        case 'danger':
            return {
                background: 'rgba(234, 140, 26, 0.12)',
                color: '#b45f06',
                border: 'rgba(234, 140, 26, 0.24)',
            };
        default:
            return {
                background: 'rgba(107, 114, 128, 0.10)',
                color: '#4b5563',
                border: 'rgba(107, 114, 128, 0.18)',
            };
    }
}

const PacksSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const PacksToolbar = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const PacksToolbarText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
`;

const PacksToolbarTitle = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-body);
`;

const PacksList = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
`;

const PackCard = styled.button<{ $tone: PackStatusTone }>`
    position: relative;
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--spacer-M);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: 4px solid ${props => resolvePackAccent(props.$tone)};
    border-radius: 14px;
    background: linear-gradient(180deg, white 0%, var(--bg-light, #f8f9fa) 100%);
    padding: var(--spacer-S) calc(var(--spacer-M) + 200px) var(--spacer-S) var(--spacer-M);
    text-align: left;
    cursor: pointer;
    appearance: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);

    @media (max-width: 640px) {
        padding-right: var(--spacer-M);
    }

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
        border-color: var(--brand-primary, #2ab7af);
    }

    &:disabled {
        cursor: default;
    }
`;

const PackMain = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const PackHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    flex-wrap: wrap;
    min-width: 0;
`;

const PackName = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-body);
    line-height: 1.25;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PackMetaRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: var(--spacer-S);
`;

const PackMetaItem = styled.div`
    min-width: 0;
    font-size: 0.84rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PackMetaSecondary = styled(PackMetaItem)`
    font-size: 0.78rem;
    font-style: italic;
`;

const PackRightMeta = styled.div`
    position: absolute;
    top: 50%;
    right: var(--spacer-M);
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    min-width: 0;
    width: 190px;

    @media (max-width: 640px) {
        position: static;
        top: auto;
        right: auto;
        transform: none;
        align-items: flex-start;
        width: auto;
        margin-top: var(--spacer-XXS);
    }
`;

const PackBadge = styled(Chip)<{ $tone: PackStatusTone }>`
    && {
        height: 24px;
        border-radius: 999px;
        font-weight: 700;
        background: ${props => resolvePackBadgeStyles(props.$tone).background};
        color: ${props => resolvePackBadgeStyles(props.$tone).color};
        border: 1px solid ${props => resolvePackBadgeStyles(props.$tone).border};

        .MuiChip-label {
            padding-left: var(--spacer-XS);
            padding-right: var(--spacer-XS);
            font-size: 0.74rem;
        }
    }
`;

const EmptyStateCard = styled.div`
    border: var(--border-thin) dashed var(--appLigthGrey);
    border-radius: 14px;
    background: var(--bg-light, #f8f9fa);
    padding: var(--spacer-M);
    color: var(--text-muted);
    font-style: italic;
`;

const ClientPacksTable : FC<ClientPacksTableProps> = ({isOpen = true, client, clientIsProfessional}) => {

    const t = useTranslations();
    const dispatch = useDispatch();
    const taxes = useTaxData();
    const canEdit = useSelector(clientIsProfessional ? canEditProfessionals : canEditAppointments)
    const canSeePrice = useSelector(canSeePrices)
    const canSeeInvoice = useSelector(canSeeInvoices)
    const packs = useSelector(clientIsProfessional ? getPacksForProfessional(client.id) : getPacksForClient(client.id));
    const { templates } = usePackTemplates()
    const loading = useSelector(isLoadingEntities('clientPacks'))
    
    const [addPackDropdown, setAddPackDropdown] = useState(false);
    const sortedPacks = useMemo(() => [...(packs ?? [])].sort((a, b) => resolvePackSortValueSafe(b) - resolvePackSortValueSafe(a)), [packs]);

    const packsWithMissingPayment = useMemo(() => canSeePrice
        ? (packs ?? []).filter(p => p.price > 0 && getTotalPayments(p.payments) < p.price * (1 + p.vat))
        : [], [packs, canSeePrice])

    const packsWithoutInvoice = useMemo(() => canSeeInvoice
        ? (packs ?? []).filter(p => p.price > 0 && !(p.invoices ?? []).length)
        : [], [packs, canSeeInvoice])

    const missingPaymentIds = useMemo(() => new Set(packsWithMissingPayment.map(p => p.id)), [packsWithMissingPayment])
    const missingInvoiceIds = useMemo(() => new Set(packsWithoutInvoice.map(p => p.id)), [packsWithoutInvoice])
    const sortedTemplates = useMemo(() => [...templates].sort((a, b) => a.name.localeCompare(b.name)), [templates]);
    
    useEffect(() => {
        if(isOpen) {
            dispatch(loadEntities(
                'clientPacks', 
                pageSize => clientIsProfessional
                    ? getProfessionalPacksAsync(client.id, pageSize, 0)
                    : getClientPacksAsync(client.id, pageSize, 0), 
                'NewResult',
                e => e.clientId === client.id));
        }
    }, [dispatch, client.id, clientIsProfessional, isOpen]);
    
    const handlePackClick = useCallback((pack: ClientPack) => () => {
        dispatch(entityEditing('clientPacks', pack.id));
    }, [dispatch]);
    
    const toggleAddPackDropdown = useCallback(() => setAddPackDropdown(prev => !prev), [setAddPackDropdown]);
    
    const handleAddPackClick = useCallback((packSettings: PackSettings) => () => {
        toggleAddPackDropdown();
        const initialData: ClientPack = {
            id: '',
            packTemplateId: packSettings.templateId ?? packSettings.id,
            packName: packSettings.name,
            packVersion: packSettings.version,
            appointmentTypes: packSettings.appointmentTypes,
            price: packSettings.price,
            vat: (taxes.taxRatesMap[packSettings.invoiceItemDetails?.taxId ?? '']?.rate ?? 0) / 100,
            totalMinutesStillAvailable: packSettings.totalMinutes ?? 0,
            totalCreditsStillAvailable: packSettings.totalCredits ?? 0,
            creationDate: getNowDateString(),
            createdBy: '', // will be filled up in the server
            clientId: client.id,
            clientName: client.name,
            clientIsProfessional,
            extras: [],
            validityFrom: getNowDateString(),
            validityTo: packSettings?.validityDays && packSettings.validityDays >= 30
                ? moment().add(packSettings.validityDays / 30, 'months').add(-1, 'day').toISOString()
                : packSettings?.validityDays ? moment().add(packSettings.validityDays - 1, 'days').toISOString()
                : undefined,
        }
        dispatch(entityCreating('clientPacks', initialData));
    }, [dispatch, toggleAddPackDropdown]);

    return (
        <>
            <PacksSection>
                <PacksToolbar>
                    <PacksToolbarText>
                        <PacksToolbarTitle>{t(TK.packs)}/{t(TK.subscriptions)}</PacksToolbarTitle>
                    </PacksToolbarText>
                    {canEdit && (
                        <Dropdown isOpen={addPackDropdown} toggle={toggleAddPackDropdown} style={{display: 'inline'}}>
                            <DropdownToggle tag="span">
                                <AppButton id={`client-${client.id}-add-pack-button`}>{t(TK.addPack)}</AppButton>
                            </DropdownToggle>
                            <DropdownMenu>
                                {sortedTemplates.map(packSettings => (
                                        <DropdownItem
                                            id={`client-${client.id}-add-pack-${sanitize(packSettings.name)}-button`}
                                            key={packSettings.templateId ?? packSettings.id} onClick={handleAddPackClick(packSettings)}
                                        >
                                            {packSettings.name}
                                        </DropdownItem>
                                    ))
                                }
                            </DropdownMenu>
                        </Dropdown>
                    )}
                </PacksToolbar>

{ loading ? <Typography><i>{t(TK.loading)}...</i></Typography> :
                  !sortedPacks.length ? <EmptyStateCard>{t(TK.noPackAddedYet)}</EmptyStateCard> :
                    <PacksList>
                        { sortedPacks.map(pack => {
                            const tone = getPackStatusTone(pack);
                            const availabilityText = getPackAvailabilityText(t, pack);
                            const acquiredOnText = resolvePackAcquiredDate(pack);
                            const validityFromText = resolvePackValidityFrom(pack);
                            const validityToText = resolvePackValidityTo(pack);
                            const hasMissingPayment = missingPaymentIds.has(pack.id);
                            const hasMissingInvoice = missingInvoiceIds.has(pack.id);

                            return (
                                <PackCard
                                    key={pack.id}
                                    type="button"
                                    $tone={tone}
                                    id={`client-${client.id}-pack-${pack.id}-open-button`}
                                    onClick={handlePackClick(pack)}
                                >
                                    <PackMain>
                                        <PackHeaderRow>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', minWidth: 0 }}>
                                                <PackName>{pack.packName}</PackName>
                                                {hasMissingPayment && (
                                                    <PackBadge size="small" $tone="warning" label={t(TK.missingPayment)} />
                                                )}
                                                {hasMissingInvoice && (
                                                    <PackBadge size="small" $tone="warning" label={t(TK.withoutInvoice)} />
                                                )}
                                            </div>
                                        </PackHeaderRow>
                                        <PackMetaRow>
                                            {!!validityFromText && (
                                                <PackMetaItem>{t(TK.validFromTo, validityFromText, validityToText || t(TK.unknown))}</PackMetaItem>
                                            )}
                                            <PackRightMeta>
                                                {!!availabilityText && <PackBadge size="small" $tone={tone} label={availabilityText.replace(/^\(|\)$/g, '')} />}
                                                {!!acquiredOnText && (
                                                    <PackMetaSecondary>{t(TK.acquiredOn, acquiredOnText)}</PackMetaSecondary>
                                                )}
                                            </PackRightMeta>
                                        </PackMetaRow>
                                    </PackMain>
                                </PackCard>
                            );
                        })}
                    </PacksList>
                }
            </PacksSection>
        </>
    );
}

export default ClientPacksTable;
